import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import type { AssistantToolCall } from '@aeris/contracts'
import {
  Badge,
  QueryState,
  Section,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchAssistantToolCalls } from '../lib/api.ts'
import { formatDayTime } from '../lib/date-format.ts'

/**
 * O que o Claude e o assistente consultaram (F295).
 *
 * Em 16/10 o assistente errou a mesma consulta cinco vezes e ninguém viu: o
 * registro existia, mas só no banco. Aqui o dono vê quem perguntou, qual
 * consulta, se deu certo e quanto demorou.
 */

const CHANNEL_LABELS: Record<AssistantToolCall['channel'], string> = {
  mcp: 'Claude',
  whatsapp: 'WhatsApp',
  digest: 'Resumo do dia',
}

const STATUS: Record<
  string,
  { label: string; tone: 'success' | 'warning' | 'danger' }
> = {
  ok: { label: 'Certo', tone: 'success' },
  invalid: { label: 'Pedido errado', tone: 'warning' },
  denied: { label: 'Sem acesso', tone: 'warning' },
  too_big: { label: 'Grande demais', tone: 'warning' },
  failed: { label: 'Falhou', tone: 'danger' },
}

export const AssistantToolCalls = (): ReactNode => {
  const calls = useQuery({
    queryKey: ['assistant-tool-calls'],
    queryFn: fetchAssistantToolCalls,
  })
  const list = calls.data?.calls ?? []

  return (
    <Section
      title="O que foi consultado"
      description="As últimas 100 consultas do Claude, do WhatsApp do assistente e do resumo do dia: quem, qual consulta e se deu certo."
    >
      <QueryState
        isPending={calls.isPending}
        isError={calls.isError}
        error={calls.error}
        onRetry={() => void calls.refetch()}
        what="as consultas do assistente"
        isEmpty={list.length === 0}
        empty={
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Ninguém consultou ainda.
          </p>
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Quando</TableHeaderCell>
                <TableHeaderCell>Quem</TableHeaderCell>
                <TableHeaderCell>Por onde</TableHeaderCell>
                <TableHeaderCell>Consulta</TableHeaderCell>
                <TableHeaderCell>Resultado</TableHeaderCell>
                <TableHeaderCell>Tempo</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((call) => {
                const status = STATUS[call.status] ?? {
                  label: call.status,
                  tone: 'warning' as const,
                }
                return (
                  <TableRow key={call.id}>
                    <TableCell>{formatDayTime(call.createdAt)}</TableCell>
                    <TableCell>{call.userName ?? '—'}</TableCell>
                    <TableCell>{CHANNEL_LABELS[call.channel]}</TableCell>
                    <TableCell>
                      <span title={call.args}>{call.tool}</span>
                    </TableCell>
                    <TableCell>
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </TableCell>
                    <TableCell>{`${String(call.durationMs)} ms`}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>
    </Section>
  )
}
